import { QBS_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  ContentCopyRoundedIcon,
  DeleteOutlineRoundedIcon,
  DoDisturbAltOutlinedIcon,
  IconButton,
  type IconButtonProps,
  Menu,
  MenuItem,
  mergeSx,
  MoreVertIcon,
  Text,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useState } from 'react';

import MenuItemWithConfirmation from './menu-item-with-confirmation';

interface QBankStructureLevelItemRowMenuProps extends IconButtonProps {
  onDuplicate?: () => void;
  onDelete?: () => void;
  onSetNonInfo?: () => void;
  disabledSetNonInfo?: boolean;
}

export default function QBankStructureLevelItemRowMenu(
  props: Readonly<QBankStructureLevelItemRowMenuProps>,
) {
  const {
    onDuplicate,
    onDelete,
    onSetNonInfo,
    disabledSetNonInfo: isSetNonInfoDisabled,
    sx,
    ...restProps
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const isUpdatable = useHasPermission(APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE);

  const shouldDisableDelete = typeof onDelete !== 'function';

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    //FIXME: When the menu is closed, it should perform something like stopPropagation() to prevent the event from bubbling up the DOM tree.
    setAnchorEl(null);
  };

  const handleDuplicate = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    onDuplicate?.();
    handleCloseMenu();
  };

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete?.();
    handleCloseMenu();
  };

  const handleDiscardDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleCloseMenu();
  };

  const handleSetNonInfo = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    onSetNonInfo?.();
    handleCloseMenu();
  };

  return (
    <>
      <IconButton
        onClick={handleOpenMenu}
        sx={mergeSx(
          {
            color: (theme) => theme.palette.grey[500],
          },
          sx,
        )}
        {...restProps}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '160px',
            },
          },
        }}
      >
        {onSetNonInfo && isUpdatable ? (
          <MenuItem
            data-testid={QBS_DETAILS_DATA_TEST_ID.SET_NON_INFO_MENU}
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.primary.lighter,
              },
            })}
            onClick={handleSetNonInfo}
            disabled={isSetNonInfoDisabled}
          >
            <DoDisturbAltOutlinedIcon sx={(theme) => ({ mr: theme.spacing(1) })} />
            None (0)
          </MenuItem>
        ) : null}
        {onDuplicate && isUpdatable ? (
          <MenuItem
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.primary.lighter,
              },
            })}
            onClick={handleDuplicate}
          >
            <ContentCopyRoundedIcon sx={(theme) => ({ mr: theme.spacing(1) })} />
            Duplicate
          </MenuItem>
        ) : null}
        {onDelete && isUpdatable ? (
          <MenuItemWithConfirmation
            sx={(theme) => ({
              color: theme.palette.error.main,
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.lighter,
              },
            })}
            onAccept={handleDelete}
            onDiscard={handleDiscardDelete}
            title='Delete Structure Level'
            content={
              <Text>
                การ Delete Structure Level&nbsp;
                <Text component='span' variant='body1Semibold'>
                  จะทำให้ข้อมูลที่อยู่ภายใต้ระดับนั้น
                  <br />
                  ทั้งหมดหายไป
                </Text>
                กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
              </Text>
            }
            acceptText='Delete'
            discardText='Cancel'
            variant='error'
            disabled={shouldDisableDelete}
          >
            <DeleteOutlineRoundedIcon sx={(theme) => ({ mr: theme.spacing(1) })} />
            Delete
          </MenuItemWithConfirmation>
        ) : null}
      </Menu>
    </>
  );
}
